import React, { useState, useEffect } from 'react';
import {
  CardBody,
  Input,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import {
  COUNTER_ACCENT,
  COUNTER_ICON_BTN_STYLE,
} from '../../../../../shared/offer-domain/constants';
import ActionButton from '../../../../../Components/Common/ActionButton';
import { SkeletonTable } from '../../../../../Components/Common/Skeleton';
import CounterOfferModal from '../CounterOfferModal';
import { getRowKey } from '../../../hooks/modelDeliveryTable/shared';
import {
  formatIncotermRoute,
  formatFobLocationsJoined,
  hasCounterOffer,
  isCounterValueChanged,
  isCounterQuantityChanged,
  isCounterDateChanged,
  isFobOriginDisplay,
  resolveCapableLocationSelections,
} from '../shared/utils';
import {
  cellClass,
  headerCellClass,
  renderPortCell,
  renderSelectedOriginLocations,
  createNumberInputRenderer,
} from '../shared/tableRenderHelpers';
import {
  COUNTER_BADGE_STYLE,
  COUNTER_ROW_MARK_STYLE,
  DELIVERY_TABLE_STYLE,
  DELIVERY_MAIN_TABLE_STYLE,
  DeliveryThLabel,
  getNonLpgColStyles,
  getNonLpgCounterColStyles,
  withHeaderStyle,
} from '../shared/deliveryTableLayout';

const getTableColSpan = (showCounterOfferColumn, alphaCols) => {
  const base = 9 + alphaCols;
  return showCounterOfferColumn ? base + 1 : base;
};

const isFobIncotermRow = data =>
  String(data?.incoterm || '')
    .trim()
    .toUpperCase() === 'FOB';

const isCounterFobIncoterm = data =>
  String(data?.counterIncoterm || '')
    .trim()
    .toUpperCase() === 'FOB';

const getRowOriginLocations = data =>
  (Array.isArray(data?.incotermFOBLocations) ? data.incotermFOBLocations : [])
    .map(loc => ({
      id: String(loc?.id ?? loc?.Id ?? ''),
      name: loc?.name ?? loc?.Name ?? '',
    }))
    .filter(loc => loc.id && loc.name);

const getRowOriginNames = data =>
  getRowOriginLocations(data)
    .map(loc => loc.name)
    .filter(Boolean);

const resolveLoadingPortLabel = (data, selectedOrigins) => {
  if (data?.fobOriginLocationName) {
    return data.fobOriginLocationName;
  }
  if (data?.incotermLocationId) {
    const fromSelected = Array.isArray(selectedOrigins)
      ? selectedOrigins.find(
          loc => String(loc?.id) === String(data.incotermLocationId)
        )
      : null;
    return fromSelected?.name || data?.loadingPort || '-';
  }
  const names = getRowOriginNames(data);
  if (isFobOriginDisplay(data?.route, names)) {
    const selectedNames = Array.isArray(selectedOrigins)
      ? selectedOrigins.map(loc => loc?.name || loc).filter(Boolean)
      : selectedOrigins?.name
        ? [selectedOrigins.name]
        : selectedOrigins
          ? [selectedOrigins]
          : [];
    return renderSelectedOriginLocations(selectedNames);
  }
  if (isFobIncotermRow(data) || names.length) {
    return formatFobLocationsJoined(names) || data?.loadingPort || '-';
  }
  return '-';
};

const SelectOriginLocationModal = ({
  isOpen,
  toggle,
  locations = [],
  selectedIds = [],
  onToggle,
  onConfirm,
}) => {
  const selectedSet = new Set((selectedIds || []).map(String));

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size='md'>
      <ModalHeader
        toggle={toggle}
        className='fw-semibold'
        style={{ borderBottom: '1px solid var(--vz-border-color, #e5e7eb)' }}
      >
        <i className='ri-map-pin-line me-2' style={{ color: '#0d7c66' }} />
        Select Origin Location
      </ModalHeader>
      <ModalBody className='py-4'>
        {locations.length === 0 ? (
          <p
            className='text-muted text-center py-3 mb-0'
            style={{ fontSize: '13px' }}
          >
            No origin locations available
          </p>
        ) : (
          <div
            className='table-responsive'
            style={{ maxHeight: '407px', overflowY: 'auto' }}
          >
            <table
              aria-label='Locations'
              className='align-middle table-nowrap mb-0'
              style={{ width: '100%' }}
            >
              <thead className='table-light'>
                <tr>
                  <th style={{ width: '50px' }}>
                    <span className='visually-hidden'>Select</span>
                  </th>
                  <th>Location Name</th>
                </tr>
              </thead>
              <tbody>
                {locations.map(loc => (
                  <tr
                    key={loc.id}
                    style={{
                      borderBottom: '1px solid var(--vz-border-color, #e5e7eb)',
                    }}
                  >
                    <td className='text-center'>
                      <Input
                        type='checkbox'
                        checked={selectedSet.has(String(loc.id))}
                        onChange={() => onToggle(loc)}
                        aria-label={`Select ${loc.name}`}
                      />
                    </td>
                    <td>{loc.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ModalBody>
      <ModalFooter
        style={{ borderTop: '1px solid var(--vz-border-color, #e5e7eb)' }}
      >
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-close-line'
          onClick={toggle}
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant='primary'
          size='sm'
          icon='ri-check-line'
          disabled={!selectedIds?.length}
          onClick={onConfirm}
        >
          Confirm
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

const getIncotermGroupKey = (data, index) =>
  String(data?.quantityIncotermId ?? `row-${index}`);

const ModelDeliveryTableNonLpg = ({
  embedded = false,
  isDeliveryEditable,
  isLoading = false,
  searchTerm,
  setSearchTerm,
  filteredItems,
  capableState,
  expandedRows,
  counterModalOpen,
  counterFormData,
  counterHasChanges,
  counterFormErrors,
  counterFormLoading,
  form,
  usesDyedUndyed = true,
  showDyed = true,
  showUndyed = true,
  pricingUnit = null,
  alphaColumnLabel = 'Alpha',
  incotermTypeOptions,
  incotermRouteOptions,
  incotermLocationOptions,
  fobRouteOptions,
  handleInputChange,
  handleCapableChange,
  toggleRowExpand,
  handleCounterFormChange,
  setCounterPortSetup,
  handleSaveCounter,
  handleDeleteCounter,
  openCounterModal,
  closeCounterModal,
}) => {
  const alphaSuffix = pricingUnit || 'USD/BBL';
  // Gasoline parcels may ask for only one of Dyed / Undyed. The first alpha
  // column is Dyed (gasoline) or the single alpha (other products).
  const showFirstAlpha = !usesDyedUndyed || showDyed;
  const showUndyedAlpha = usesDyedUndyed && showUndyed;
  const alphaCols = Number(showFirstAlpha) + Number(showUndyedAlpha);
  const twoAlphaCols = alphaCols === 2;
  const showCounterOfferColumn =
    isDeliveryEditable &&
    (filteredItems || []).some(
      data =>
        !(capableState[getRowKey(data)] || false) && !hasCounterOffer(data)
    );
  const col = getNonLpgColStyles({
    usesDyedUndyed: twoAlphaCols,
    showCounterOfferColumn,
  });
  const tableColSpan = getTableColSpan(showCounterOfferColumn, alphaCols);
  const renderNumberInput = createNumberInputRenderer({
    isDeliveryEditable,
    handleInputChange,
  });
  const [selectedOriginByKey, setSelectedOriginByKey] = useState({});
  const [originModal, setOriginModal] = useState(null);

  useEffect(() => {
    const next = {};
    (filteredItems || []).forEach(item => {
      const selections =
        (Array.isArray(item?.selectedCapableLocations) &&
        item.selectedCapableLocations.length
          ? item.selectedCapableLocations
          : null) || resolveCapableLocationSelections(item);
      if (!selections.length) return;
      next[getRowKey(item)] = selections.map(loc => ({
        id: String(loc.id),
        name: loc.name || '',
      }));
    });
    setSelectedOriginByKey(prev => ({ ...prev, ...next }));
  }, [filteredItems]);

  const closeOriginModal = () => setOriginModal(null);

  const confirmOriginSelection = async () => {
    const selected = originModal?.selectedLocations || [];
    if (!selected.length) return;

    const detailId = String(originModal.detailId ?? '');
    const selectedIds = selected.map(loc => loc.id);

    setSelectedOriginByKey(prev => {
      const next = { ...prev };
      (filteredItems || []).forEach(item => {
        const key = getRowKey(item);
        const match = detailId
          ? String(item.quantityInformationDetailId ?? '') === detailId
          : key === originModal.rowKey;
        if (match) delete next[key];
      });
      return next;
    });

    await handleCapableChange(
      { target: { checked: true } },
      originModal.rowKey,
      originModal.detailId,
      selectedIds
    );
    closeOriginModal();
  };

  const onCapableToggle = (e, data, rowKey) => {
    if (hasCounterOffer(data)) return;

    if (
      e.target.checked &&
      isFobOriginDisplay(data.route, getRowOriginNames(data))
    ) {
      const current = selectedOriginByKey[rowKey] || [];
      setOriginModal({
        rowKey,
        detailId: data.quantityInformationDetailId,
        locations: getRowOriginLocations(data),
        selectedLocations: Array.isArray(current)
          ? current
          : current?.id
            ? [current]
            : [],
      });
      return;
    }

    if (!e.target.checked) {
      const detailId = String(data.quantityInformationDetailId ?? '');
      setSelectedOriginByKey(prev => {
        const next = { ...prev };
        (filteredItems || []).forEach(item => {
          const key = getRowKey(item);
          const match = detailId
            ? String(item.quantityInformationDetailId ?? '') === detailId
            : key === rowKey;
          if (match) delete next[key];
        });
        return next;
      });
    }

    handleCapableChange(e, rowKey, data.quantityInformationDetailId);
  };

  const counterByIncotermGroup = {};
  (filteredItems || []).forEach((data, index) => {
    const key = getIncotermGroupKey(data, index);
    counterByIncotermGroup[key] =
      counterByIncotermGroup[key] || hasCounterOffer(data);
  });

  const renderCounterOfferRow = (data, _index) => {
    const nestedCol = getNonLpgCounterColStyles({
      usesDyedUndyed: twoAlphaCols,
      showActions: isDeliveryEditable,
    });
    const counterDeliveryBasis =
      data.counterDeliveryBasis ?? data.counterParcelSize ?? null;
    const deliveryBasisChanged = isCounterQuantityChanged(
      data.deliveryBasis,
      counterDeliveryBasis
    );
    const incotermRoute = formatIncotermRoute(
      data.counterIncoterm,
      data.counterRoute
    );
    const originalIncotermRoute = formatIncotermRoute(
      data.incoterm,
      data.route
    );
    const incotermRouteChanged =
      !!data.counterIncoterm &&
      isCounterValueChanged(originalIncotermRoute, incotermRoute);

    return (
      <tr className='bg-light'>
        <td
          colSpan={tableColSpan}
          className='p-0'
          style={COUNTER_ROW_MARK_STYLE}
        >
          <div
            className='bg-light-subtle'
            style={{ width: '100%', maxWidth: '100%' }}
          >
            <div className='px-2 py-2 w-100'>
              <table
                aria-label='Counter offer'
                className='table table-sm table-borderless mb-0 align-middle w-100'
                style={DELIVERY_TABLE_STYLE}
              >
                <thead>
                  <tr className='text-muted border-bottom'>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.expand)}
                    >
                      <span className='visually-hidden'>Type</span>
                    </th>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.basis)}
                    >
                      <DeliveryThLabel
                        title='Delivery Basis'
                        lines={['Delivery', 'Basis']}
                      />
                    </th>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.incoterm)}
                    >
                      <DeliveryThLabel
                        title='Incoterm - Route'
                        lines={['Incoterm', 'Route']}
                      />
                    </th>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.loading)}
                    >
                      <DeliveryThLabel
                        title='Loading Port'
                        lines={['Loading', 'Port']}
                      />
                    </th>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.port1)}
                    >
                      Port 1
                    </th>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.port2)}
                    >
                      Port 2
                    </th>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.port3)}
                    >
                      Port 3
                    </th>
                    {showFirstAlpha && (
                      <th
                        className={headerCellClass}
                        style={withHeaderStyle(nestedCol.alpha)}
                      >
                        {usesDyedUndyed ? (
                          <DeliveryThLabel
                            title='Alpha Dyed'
                            lines={['Alpha', 'Dyed']}
                          />
                        ) : (
                          <DeliveryThLabel
                            title={alphaColumnLabel}
                            lines={String(alphaColumnLabel)
                              .split(/\s+/)
                              .slice(0, 2)}
                          />
                        )}
                      </th>
                    )}
                    {showUndyedAlpha && (
                      <th
                        className={headerCellClass}
                        style={withHeaderStyle(nestedCol.alpha)}
                      >
                        <DeliveryThLabel
                          title='Alpha Undyed'
                          lines={['Alpha', 'Undyed']}
                        />
                      </th>
                    )}
                    {isDeliveryEditable && (
                      <th
                        className={headerCellClass}
                        style={withHeaderStyle(nestedCol.counter)}
                      >
                        <span className='visually-hidden'>Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      className='ps-1 py-2 align-top text-center'
                      style={{
                        ...nestedCol.expand,
                        overflow: 'hidden',
                        maxWidth: nestedCol.expand.width,
                      }}
                    >
                      <span
                        className='badge rounded-pill fw-semibold'
                        style={COUNTER_BADGE_STYLE}
                      >
                        Counter
                      </span>
                    </td>
                    <td
                      className={`fs-12 py-2 pe-2 align-top text-break ${deliveryBasisChanged ? 'fw-semibold' : 'fw-medium'}`}
                      style={{
                        color: deliveryBasisChanged
                          ? COUNTER_ACCENT
                          : undefined,
                        wordBreak: 'break-word',
                      }}
                    >
                      {counterDeliveryBasis != null &&
                      counterDeliveryBasis !== ''
                        ? counterDeliveryBasis
                        : '-'}
                    </td>
                    <td
                      className={`fs-12 py-2 pe-2 align-top text-break ${incotermRouteChanged ? 'fw-semibold' : ''}`}
                      style={{
                        color: incotermRouteChanged
                          ? COUNTER_ACCENT
                          : undefined,
                        wordBreak: 'break-word',
                      }}
                    >
                      {incotermRoute}
                    </td>
                    <td
                      className={`fs-12 py-2 pe-2 align-top text-break fw-medium ${
                        isCounterFobIncoterm(data) &&
                        isCounterValueChanged(
                          data.loadingPort,
                          data.counterLoadingPort
                        )
                          ? 'fw-semibold'
                          : ''
                      }`}
                      style={{
                        color:
                          isCounterFobIncoterm(data) &&
                          isCounterValueChanged(
                            data.loadingPort,
                            data.counterLoadingPort
                          )
                            ? COUNTER_ACCENT
                            : undefined,
                        wordBreak: 'break-word',
                      }}
                    >
                      {isCounterFobIncoterm(data)
                        ? data.counterLoadingPort || '-'
                        : '-'}
                    </td>
                    <td className='py-2 pe-2 align-top' style={nestedCol.port1}>
                      {renderPortCell(
                        data.counterFobLocationNames?.length
                          ? null
                          : data.counterLoadingPort,
                        data.counterParcelSize,
                        {
                          portLabel: 'Location',
                          startDate: data.counterStartDate,
                          endDate: data.counterEndDate,
                          fallbackDate: data.counterAddAld,
                          locationNames: data.counterFobLocationNames,
                          route: data.counterRoute,
                          highlight: {
                            port: isCounterValueChanged(
                              data.port1 || data.loadingPort,
                              data.counterLoadingPort
                            ),
                            parcelSize: isCounterQuantityChanged(
                              data.parcelSize,
                              data.counterParcelSize
                            ),
                            date: isCounterDateChanged(
                              data.port1StartDate,
                              data.port1EndDate,
                              data.counterStartDate,
                              data.counterEndDate,
                              data.addAld,
                              data.counterAddAld
                            ),
                          },
                        }
                      )}
                    </td>
                    <td className='py-2 pe-2 align-top' style={nestedCol.port2}>
                      {renderPortCell(
                        data.counterPort2,
                        data.counterParcelSize2,
                        {
                          startDate: data.counterStartDate2,
                          endDate: data.counterEndDate2,
                          fallbackDate: data.counterAddAld2,
                          highlight: {
                            port: isCounterValueChanged(
                              data.port2,
                              data.counterPort2
                            ),
                            parcelSize: isCounterQuantityChanged(
                              data.parcelSize2,
                              data.counterParcelSize2
                            ),
                            date: isCounterDateChanged(
                              data.port2StartDate,
                              data.port2EndDate,
                              data.counterStartDate2,
                              data.counterEndDate2,
                              data.addAld2,
                              data.counterAddAld2
                            ),
                          },
                        }
                      )}
                    </td>
                    <td className='py-2 pe-2 align-top' style={nestedCol.port3}>
                      {renderPortCell(
                        data.counterPort3,
                        data.counterParcelSize3,
                        {
                          startDate: data.counterStartDate3,
                          endDate: data.counterEndDate3,
                          fallbackDate: data.counterAddAld3,
                          highlight: {
                            port: isCounterValueChanged(
                              data.port3,
                              data.counterPort3
                            ),
                            parcelSize: isCounterQuantityChanged(
                              data.parcelSize3,
                              data.counterParcelSize3
                            ),
                            date: isCounterDateChanged(
                              data.port3StartDate,
                              data.port3EndDate,
                              data.counterStartDate3,
                              data.counterEndDate3,
                              data.addAld3,
                              data.counterAddAld3
                            ),
                          },
                        }
                      )}
                    </td>
                    {showFirstAlpha && (
                      <td
                        className='py-2 pe-2 align-top'
                        style={nestedCol.alpha}
                      >
                        {usesDyedUndyed
                          ? renderNumberInput({
                              rowKey: getRowKey(data),
                              row: data,
                              field: 'counterDyed',
                              value: data.counterDyed,
                              suffix: 'USD/bbl',
                            })
                          : renderNumberInput({
                              rowKey: getRowKey(data),
                              row: data,
                              field: 'counterAlpha',
                              value: data.counterAlpha ?? data.alpha2 ?? '',
                              suffix: alphaSuffix,
                            })}
                      </td>
                    )}
                    {showUndyedAlpha && (
                      <td
                        className='py-2 pe-2 align-top'
                        style={nestedCol.alpha}
                      >
                        {renderNumberInput({
                          rowKey: getRowKey(data),
                          row: data,
                          field: 'counterUndyed',
                          value: data.counterUndyed,
                          suffix: 'USD/bbl',
                        })}
                      </td>
                    )}
                    {isDeliveryEditable && (
                      <td
                        className='text-center py-2 pe-1 align-top'
                        style={nestedCol.counter}
                      >
                        <div className='d-flex justify-content-center gap-1 flex-wrap'>
                          <ActionButton
                            variant='icon'
                            size='sm'
                            icon='ri-edit-line'
                            label='Edit Counter Offer'
                            style={COUNTER_ICON_BTN_STYLE}
                            onClick={() => openCounterModal(data)}
                          />
                          <ActionButton
                            variant='icon'
                            size='sm'
                            danger
                            confirm={false}
                            icon='ri-delete-bin-line'
                            label='Delete Counter Offer'
                            onClick={() => handleDeleteCounter(data)}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const renderNoCounterOfferRow = () => (
    <tr className='bg-light'>
      <td colSpan={tableColSpan} className='p-0' style={COUNTER_ROW_MARK_STYLE}>
        <div className='px-4 py-4 text-muted fs-13 text-center'>
          There is no counteroffer for the requirement mentioned above.
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <CardBody className={embedded ? 'p-0' : 'px-0'}>
        {!embedded && (
          <div className='d-flex align-items-center mb-3'>
            <div
              className='position-relative'
              style={{ width: '280px', maxWidth: '100%' }}
            >
              <i className='ri-search-line position-absolute top-50 translate-middle-y text-muted ms-2' />
              <Input
                type='text'
                placeholder='Search'
                className='ps-5 rounded-3'
                style={{ height: '36px' }}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        )}

        <div
          className={
            embedded
              ? 'table-responsive w-100'
              : 'table-responsive table-card border rounded-3 w-100'
          }
        >
          <table
            aria-label='Delivery offers'
            className='table table-hover table-centered align-middle mb-0 w-100'
            style={DELIVERY_MAIN_TABLE_STYLE}
          >
            <thead className='table-light'>
              <tr>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.expand)}
                >
                  <span className='visually-hidden'>Expand</span>
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.basis)}
                >
                  <DeliveryThLabel
                    title='Delivery Basis'
                    lines={['Delivery', 'Basis']}
                  />
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.incoterm)}
                >
                  <DeliveryThLabel
                    title='Incoterm - Route'
                    lines={['Incoterm', 'Route']}
                  />
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.loading)}
                >
                  <DeliveryThLabel
                    title='Loading Port'
                    lines={['Loading', 'Port']}
                  />
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.port)}
                >
                  Port 1
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.port)}
                >
                  Port 2
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.port)}
                >
                  Port 3
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.capable)}
                >
                  <DeliveryThLabel
                    title='Is Capable?'
                    lines={['Is', 'Capable?']}
                  />
                </th>
                {showFirstAlpha && (
                  <th
                    className={headerCellClass}
                    style={withHeaderStyle(col.alpha)}
                  >
                    {usesDyedUndyed ? (
                      <DeliveryThLabel
                        title='Alpha Dyed'
                        lines={['Alpha', 'Dyed']}
                      />
                    ) : (
                      <DeliveryThLabel
                        title={alphaColumnLabel}
                        lines={String(alphaColumnLabel)
                          .split(/\s+/)
                          .slice(0, 2)}
                      />
                    )}
                  </th>
                )}
                {showUndyedAlpha && (
                  <th
                    className={headerCellClass}
                    style={withHeaderStyle(col.alpha)}
                  >
                    <DeliveryThLabel
                      title='Alpha Undyed'
                      lines={['Alpha', 'Undyed']}
                    />
                  </th>
                )}
                {showCounterOfferColumn && (
                  <th
                    className={headerCellClass}
                    style={withHeaderStyle(col.counter)}
                  >
                    <DeliveryThLabel
                      title='Counter Offer'
                      lines={['Counter', 'Offer']}
                    />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonTable inline rows={3} columns={tableColSpan} />
              ) : filteredItems.length > 0 ? (
                filteredItems.map((data, index) => {
                  const rowKey = getRowKey(data);
                  const isCapable = capableState[rowKey] || false;
                  const isExpanded = expandedRows[rowKey];
                  const showCounter = hasCounterOffer(data);
                  const groupCountered =
                    !!counterByIncotermGroup[getIncotermGroupKey(data, index)];

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className={isExpanded ? 'border-bottom-0' : undefined}
                      >
                        <td
                          className={cellClass}
                          style={{
                            ...col.expand,
                            ...(showCounter ? COUNTER_ROW_MARK_STYLE : null),
                          }}
                        >
                          <button
                            type='button'
                            className='btn btn-link btn-sm p-0 text-muted'
                            onClick={() => toggleRowExpand(rowKey)}
                            aria-expanded={isExpanded}
                            aria-label={
                              isExpanded ? 'Collapse row' : 'Expand row'
                            }
                          >
                            <i
                              className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line fs-16`}
                              aria-hidden='true'
                            />
                          </button>
                        </td>
                        <td
                          className={`${cellClass} fw-medium fs-13`}
                          style={col.basis}
                        >
                          {data.deliveryBasis || `D${index + 1}`}
                        </td>
                        <td
                          className={`${cellClass} fs-13`}
                          style={col.incoterm}
                        >
                          {formatIncotermRoute(data.incoterm, data.route)}
                        </td>
                        <td
                          className={`${cellClass} fs-13 fw-medium`}
                          style={col.loading}
                        >
                          {resolveLoadingPortLabel(
                            data,
                            selectedOriginByKey[rowKey]
                          )}
                        </td>
                        <td className={cellClass} style={col.port}>
                          {renderPortCell(
                            data.port1 || data.loadingPort,
                            data.parcelSize,
                            {
                              portLabel: 'Location',
                              startDate: data.port1StartDate,
                              endDate: data.port1EndDate,
                              fallbackDate: data.addAld,
                              locationNames: data.fobLocationNames,
                              route: data.route,
                            }
                          )}
                        </td>
                        <td className={cellClass} style={col.port}>
                          {renderPortCell(data.port2, data.parcelSize2, {
                            startDate: data.port2StartDate,
                            endDate: data.port2EndDate,
                            fallbackDate: data.addAld2,
                          })}
                        </td>
                        <td className={cellClass} style={col.port}>
                          {renderPortCell(data.port3, data.parcelSize3, {
                            startDate: data.port3StartDate,
                            endDate: data.port3EndDate,
                            fallbackDate: data.addAld3,
                          })}
                        </td>
                        <td
                          className={`${cellClass} text-center`}
                          style={col.capable}
                        >
                          {!isDeliveryEditable ? (
                            isCapable ? (
                              <Badge
                                color='primary'
                                className='px-2 py-1 fs-12 fw-medium rounded-pill'
                              >
                                Compliant
                              </Badge>
                            ) : groupCountered ? (
                              <span
                                className='badge rounded-pill px-2 py-1 fs-12 fw-medium'
                                style={{
                                  backgroundColor: COUNTER_ACCENT,
                                  color: '#fff',
                                }}
                              >
                                Countered
                              </span>
                            ) : (
                              <Badge
                                color='secondary'
                                className='px-2 py-1 fs-12 fw-medium rounded-pill'
                              >
                                Not Offered
                              </Badge>
                            )
                          ) : (
                            <div className='form-check form-switch d-flex justify-content-center mb-0'>
                              <Input
                                className='form-check-input'
                                type='checkbox'
                                role='switch'
                                checked={showCounter ? false : isCapable}
                                disabled={showCounter}
                                onChange={e => onCapableToggle(e, data, rowKey)}
                              />
                            </div>
                          )}
                        </td>
                        {showFirstAlpha && (
                          <td className={cellClass} style={col.alpha}>
                            {usesDyedUndyed
                              ? renderNumberInput({
                                  rowKey,
                                  row: data,
                                  field: 'unitBbl',
                                  value: data.dyed ?? data.unitBbl,
                                  disabled: !isCapable,
                                  suffix: 'USD/bbl',
                                })
                              : renderNumberInput({
                                  rowKey,
                                  row: data,
                                  field: 'alpha',
                                  value: data.alpha || '',
                                  disabled: !isCapable,
                                  suffix: alphaSuffix,
                                })}
                          </td>
                        )}
                        {showUndyedAlpha && (
                          <td className={cellClass} style={col.alpha}>
                            {renderNumberInput({
                              rowKey,
                              row: data,
                              field: 'cbm',
                              value: data.undyed ?? data.cbm,
                              disabled: !isCapable,
                              suffix: 'USD/bbl',
                            })}
                          </td>
                        )}
                        {showCounterOfferColumn && (
                          <td
                            className={`${cellClass} text-center`}
                            style={col.counter}
                          >
                            {!showCounter && !isCapable ? (
                              <ActionButton
                                variant='icon'
                                size='sm'
                                icon='ri-edit-line'
                                label='Counter Offer'
                                style={COUNTER_ICON_BTN_STYLE}
                                onClick={() => openCounterModal(data)}
                              />
                            ) : null}
                          </td>
                        )}
                      </tr>
                      {isExpanded &&
                        !isCapable &&
                        (showCounter
                          ? renderCounterOfferRow(data, index)
                          : renderNoCounterOfferRow())}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    className='text-center text-muted py-4'
                  >
                    {searchTerm
                      ? 'No data matching your search'
                      : 'No data available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardBody>

      <CounterOfferModal
        isOpen={counterModalOpen}
        toggle={closeCounterModal}
        formData={counterFormData}
        saveDisabled={!counterHasChanges}
        errors={counterFormErrors}
        onFormChange={handleCounterFormChange}
        onPortSetup={setCounterPortSetup}
        onSave={handleSaveCounter}
        loading={counterFormLoading}
        form={form}
        incotermTypeOptions={incotermTypeOptions}
        incotermRouteOptions={incotermRouteOptions}
        incotermLocationOptions={incotermLocationOptions}
        fobRouteOptions={fobRouteOptions}
      />
      <SelectOriginLocationModal
        isOpen={Boolean(originModal)}
        toggle={closeOriginModal}
        locations={originModal?.locations ?? []}
        selectedIds={(originModal?.selectedLocations || []).map(loc => loc.id)}
        onToggle={loc =>
          setOriginModal(prev => {
            if (!prev) return prev;
            const current = prev.selectedLocations || [];
            const exists = current.some(item => item.id === loc.id);
            return {
              ...prev,
              selectedLocations: exists
                ? current.filter(item => item.id !== loc.id)
                : [...current, loc],
            };
          })
        }
        onConfirm={confirmOriginSelection}
      />
    </>
  );
};

export default ModelDeliveryTableNonLpg;
